import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  MoreVertical,
  FileText,
  Upload,
  Printer,
  Eye,
  Edit,
  Trash2
} from "lucide-react";
 

const PaymentVouchers = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<any>(null);

  // Mock data for payment vouchers
  const paymentVouchers = [
    {
      id: "PV-2024-001",
      date: "2024-03-15",
      payee: "ABC Suppliers Ltd",
      purpose: "Office furniture purchase",
      amount: 45000,
      category: "Equipment",
      department: "Administration",
      paymentMethod: "Bank Transfer",
      status: "approved",
      approvalLevel: 3,
      approvers: [
        { name: "Bursar", status: "approved", date: "2024-03-15" },
        { name: "Accountant", status: "approved", date: "2024-03-15" },
        { name: "Principal", status: "approved", date: "2024-03-16" }
      ],
      paymentStatus: "paid",
      paidDate: "2024-03-17",
      reference: "TXN123456",
      documents: ["invoice.pdf", "receipt.pdf"],
      createdBy: "Finance Officer",
      notes: "Delivered and installed successfully"
    },
    {
      id: "PV-2024-002",
      date: "2024-03-18",
      payee: "Tech Solutions Inc",
      purpose: "Computer lab equipment",
      amount: 125000,
      category: "Equipment",
      department: "ICT",
      paymentMethod: "Cheque",
      status: "pending",
      approvalLevel: 2,
      approvers: [
        { name: "Bursar", status: "approved", date: "2024-03-18" },
        { name: "Accountant", status: "pending", date: null },
        { name: "Principal", status: "pending", date: null }
      ],
      paymentStatus: "pending",
      paidDate: null,
      reference: "CHQ789012",
      documents: ["proforma.pdf"],
      createdBy: "HOD ICT",
      notes: "Urgent - semester starting soon"
    },
    {
      id: "PV-2024-003",
      date: "2024-03-16",
      payee: "Power Utility Company",
      purpose: "Electricity bill - February 2024",
      amount: 35000,
      category: "Utilities",
      department: "Administration",
      paymentMethod: "Bank Transfer",
      status: "rejected",
      approvalLevel: 2,
      approvers: [
        { name: "Bursar", status: "approved", date: "2024-03-16" },
        { name: "Accountant", status: "rejected", date: "2024-03-17" },
        { name: "Principal", status: "pending", date: null }
      ],
      paymentStatus: "cancelled",
      paidDate: null,
      reference: null,
      documents: ["bill.pdf"],
      createdBy: "Admin Officer",
      notes: null,
      rejectionReason: "Amount exceeds budget allocation. Please verify bill."
    }
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-3 w-3 mr-1" />
          Approved
        </Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          <XCircle className="h-3 w-3 mr-1" />
          Rejected
        </Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge variant="default">Paid</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending Payment</Badge>;
      case 'partially_paid':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">Partially Paid</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="text-2xl font-bold">Payment Vouchers</h2>
          <p className="text-sm text-muted-foreground">Manage all payment vouchers and approvals</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create PV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Payment Voucher</DialogTitle>
                <DialogDescription>Fill in the details to create a new payment voucher</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pv-date">Date</Label>
                    <Input id="pv-date" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div>
                    <Label htmlFor="pv-number">PV Number</Label>
                    <Input id="pv-number" value="PV-2024-004" disabled />
                  </div>
                </div>

                <div>
                  <Label htmlFor="payee">Payee Details</Label>
                  <Input id="payee" placeholder="Supplier, staff, or vendor name" />
                </div>

                <div>
                  <Label htmlFor="purpose">Purpose/Description</Label>
                  <Textarea id="purpose" placeholder="Detailed description of payment purpose" rows={3} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount">Amount</Label>
                    <Input id="amount" type="number" placeholder="0.00" />
                  </div>
                  <div>
                    <Label htmlFor="payment-method">Payment Method</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="bank">Bank Transfer</SelectItem>
                        <SelectItem value="mobile">Mobile Money</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="utilities">Utilities</SelectItem>
                        <SelectItem value="salaries">Salaries</SelectItem>
                        <SelectItem value="equipment">Equipment</SelectItem>
                        <SelectItem value="supplies">Supplies</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="transport">Transport</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="department">Department</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administration</SelectItem>
                        <SelectItem value="academic">Academic</SelectItem>
                        <SelectItem value="boarding">Boarding</SelectItem>
                        <SelectItem value="ict">ICT</SelectItem>
                        <SelectItem value="kitchen">Kitchen</SelectItem>
                        <SelectItem value="transport">Transport</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="reference">Reference Number (Cheque/Transaction ID)</Label>
                  <Input id="reference" placeholder="CHQ123456 or TXN789012" />
                </div>

                <div>
                  <Label>Attach Documents</Label>
                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Click to upload or drag and drop</p>
                    <p className="text-xs text-muted-foreground mt-1">Invoices, receipts, delivery notes, approval documents</p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes/Comments</Label>
                  <Textarea id="notes" placeholder="Additional notes or comments" rows={2} />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button variant="outline">Save as Draft</Button>
                  <Button onClick={() => setShowCreateDialog(false)}>Submit for Approval</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by PV number, payee, or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="utilities">Utilities</SelectItem>
                <SelectItem value="salaries">Salaries</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="supplies">Supplies</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payment Vouchers Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Voucher Register</CardTitle>
          <CardDescription>All payment vouchers with approval status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PV Number</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Payee</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Approval Status</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentVouchers.map((pv) => (
                  <TableRow key={pv.id}>
                    <TableCell className="font-medium">{pv.id}</TableCell>
                    <TableCell>{pv.date}</TableCell>
                    <TableCell>{pv.payee}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{pv.purpose}</TableCell>
                    <TableCell className="font-bold">Ksh {pv.amount.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{pv.category}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(pv.status)}</TableCell>
                    <TableCell>{getPaymentStatusBadge(pv.paymentStatus)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedVoucher(pv);
                            setShowDetailsDialog(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Download className="h-4 w-4 mr-2" />
                            Export PDF
                          </DropdownMenuItem>
                          {pv.status === 'approved' && pv.paymentStatus === 'pending' && (
                            <DropdownMenuItem>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Paid
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Void
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Voucher Details</DialogTitle>
            <DialogDescription>{selectedVoucher?.id}</DialogDescription>
          </DialogHeader>
          {selectedVoucher && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Date</Label>
                  <p className="font-medium">{selectedVoucher.date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">PV Number</Label>
                  <p className="font-medium">{selectedVoucher.id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Payee</Label>
                  <p className="font-medium">{selectedVoucher.payee}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Amount</Label>
                  <p className="font-bold text-lg">Ksh {selectedVoucher.amount.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Payment Method</Label>
                  <p className="font-medium">{selectedVoucher.paymentMethod}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Reference</Label>
                  <p className="font-medium">{selectedVoucher.reference || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Purpose</Label>
                  <p className="font-medium">{selectedVoucher.purpose}</p>
                </div>
              </div>

              <div>
                <Label className="text-lg font-semibold">Approval Workflow</Label>
                <div className="mt-3 space-y-3">
                  {selectedVoucher.approvers.map((approver: any, index: number) => (
                    <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                      {approver.status === 'approved' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : approver.status === 'rejected' ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{approver.name}</p>
                        {approver.date && (
                          <p className="text-sm text-muted-foreground">{approver.date}</p>
                        )}
                      </div>
                      {getStatusBadge(approver.status)}
                    </div>
                  ))}
                </div>
                {selectedVoucher.rejectionReason && (
                  <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <Label className="text-destructive font-semibold">Rejection Reason</Label>
                    <p className="text-sm mt-1">{selectedVoucher.rejectionReason}</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground">Attached Documents</Label>
                <div className="mt-2 space-y-2">
                  {selectedVoucher.documents.map((doc: string, index: number) => (
                    <div key={index} className="flex items-center gap-2 p-2 border rounded hover:bg-accent">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm flex-1">{doc}</span>
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {selectedVoucher.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm mt-1 p-3 bg-muted rounded">{selectedVoucher.notes}</p>
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
                {selectedVoucher.status === 'pending' && (
                  <>
                    <Button variant="destructive">Reject</Button>
                    <Button>Approve</Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentVouchers;